import { applyScopeCatalog } from "./scope-catalog.js";
import type { ChatConfig, InitialWorkspaceState } from "./types.js";

// Mirrors the AiChat:MaxMessageLength default in appsettings.json; only used when the host
// did not render the setting, so the composer never ends up without a limit.
export const DEFAULT_MAX_MESSAGE_LENGTH = 2000;

export function parseChatConfig(json: string): ChatConfig {
  const value = JSON.parse(json) as ChatConfig;
  if (
    !value.endpoints?.start ||
    !value.endpoints?.continue ||
    !value.endpoints?.articleSource ||
    !value.endpoints?.history ||
    !value.endpoints?.historyItem
  ) {
    throw new Error("Chat config is missing required endpoints.");
  }
  if (!value.labels?.assistantTitle || !value.labels?.sendButton) {
    throw new Error("Chat config is missing required labels.");
  }
  if (!value.scope?.helpCenter || !value.scope?.language) {
    throw new Error("Chat config is missing required scope groups.");
  }
  applyScopeCatalog(value.scopeCatalog);
  value.devTools = value.devTools === true;
  value.maxMessageLength = normalizeMaxMessageLength(value.maxMessageLength);
  return value;
}

function normalizeMaxMessageLength(value: unknown): number {
  return typeof value === "number" && Number.isInteger(value) && value > 0
    ? value
    : DEFAULT_MAX_MESSAGE_LENGTH;
}

export function parseInitialState(json: string): InitialWorkspaceState {
  const value = JSON.parse(json) as InitialWorkspaceState;
  return {
    mode:
      value.mode === "full"
        ? "full"
        : value.mode === "embed"
          ? "embed"
          : "prompt",
    initialQuestion: value.initialQuestion ?? "",
    entryPoint: value.entryPoint ?? "home",
    product: value.product ?? "",
    culture: value.culture ?? "nl",
    activation: value.activation ?? "",
    languageId: Number.isFinite(value.languageId) ? value.languageId : 1,
    selectedSourceUrl: value.selectedSourceUrl ?? "",
    sourceTitle: value.sourceTitle ?? "",
    query: value.query ?? "",
    filters: Array.isArray(value.filters) ? value.filters : [],
  };
}

export function readJsonScript(document: Document, id: string): string {
  const script = document.getElementById(id);
  if (!script?.textContent) {
    throw new Error(`Missing JSON script: ${id}`);
  }
  return script.textContent;
}
